import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaTrashAlt,
  FaToggleOn,
  FaToggleOff,
  FaShieldAlt,
  FaProjectDiagram,
  FaRobot,
  FaTimes,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { isPlatformAdmin } from "../../constants/roles";
import {
  FIELDS,
  VERDICTS,
  MATCH_MODES,
  RULE_EFFECTS,
  operatorsFor,
  isBooleanField,
  describeRule,
} from "../../constants/detection";
import "./DetectionRules.css";

const API = "/api/admin/detection-rules";

const emptyCondition = () => ({ field: "subject", operator: "contains", value: "" });

const emptyConditionForm = () => ({
  name: "",
  description: "",
  match: "all",
  conditions: [emptyCondition()],
  action: { effect: "raise", verdict: "Malicious" },
});

const emptyGuidanceForm = () => ({ name: "", description: "", text: "" });

/**
 * Detection Rules — self-service "detection use cases" for Platform Admins.
 * Two kinds in one screen, toggled by a segmented control:
 *   condition    deterministic field/operator/value rules (raise/suppress)
 *   ai_guidance  natural-language guidance injected into the AI prompt
 *
 * Mirrors the IocListManager CRUD/auth/role-gating pattern. Platform Admin only
 * for v1 (writes are 403 server-side for everyone else).
 */
export default function DetectionRules() {
  const { token, user } = useAuth();
  const canManage = isPlatformAdmin(user?.role);

  const [kind, setKind] = useState("condition"); // active sub-view
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [conditionForm, setConditionForm] = useState(emptyConditionForm);
  const [guidanceForm, setGuidanceForm] = useState(emptyGuidanceForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const authHeaders = useMemo(
    () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }),
    [token]
  );

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRules(json.rules || []);
    } catch (err) {
      setError(err.message || "Failed to load detection rules");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const visible = rules.filter((r) => r.kind === kind);

  if (!canManage) {
    return (
      <div className="dr-forbidden">
        <FaShieldAlt />
        <p>Detection Rules can only be managed by a Platform Admin.</p>
      </div>
    );
  }

  /* ---------- form helpers (condition builder) ---------- */
  const setCond = (idx, patch) =>
    setConditionForm((f) => {
      const conditions = f.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c));
      return { ...f, conditions };
    });

  const changeField = (idx, field) => {
    // Reset operator to the first valid one for the new field's type.
    const ops = operatorsFor(field);
    setCond(idx, { field, operator: ops[0].value, value: isBooleanField(field) ? "" : "" });
  };

  const addCond = () =>
    setConditionForm((f) => ({ ...f, conditions: [...f.conditions, emptyCondition()] }));

  const removeCond = (idx) =>
    setConditionForm((f) => ({
      ...f,
      conditions: f.conditions.length > 1 ? f.conditions.filter((_, i) => i !== idx) : f.conditions,
    }));

  const resetForms = () => {
    setConditionForm(emptyConditionForm());
    setGuidanceForm(emptyGuidanceForm());
    setFormError("");
  };

  const openForm = () => {
    resetForms();
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForms();
  };

  /* ---------- create ---------- */
  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    let payload;
    if (kind === "condition") {
      if (!conditionForm.name.trim()) return setFormError("Rule name is required.");
      const conditions = conditionForm.conditions.map((c) => ({
        field: c.field,
        operator: c.operator,
        value: isBooleanField(c.field) ? undefined : (c.value || "").trim(),
      }));
      if (conditions.some((c) => !isBooleanField(c.field) && !c.value)) {
        return setFormError("Every non-boolean condition needs a value.");
      }
      payload = {
        kind: "condition",
        name: conditionForm.name.trim(),
        description: conditionForm.description.trim() || null,
        definition: {
          match: conditionForm.match,
          conditions,
          action: { effect: conditionForm.action.effect, verdict: conditionForm.action.verdict },
        },
      };
    } else {
      if (!guidanceForm.name.trim()) return setFormError("A name is required.");
      if (!guidanceForm.text.trim()) return setFormError("Guidance text is required.");
      payload = {
        kind: "ai_guidance",
        name: guidanceForm.name.trim(),
        description: guidanceForm.description.trim() || null,
        definition: { text: guidanceForm.text.trim() },
      };
    }

    setSubmitting(true);
    try {
      const res = await fetch(API, { method: "POST", headers: authHeaders, body: JSON.stringify(payload) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRules((prev) => [json.rule, ...prev]);
      closeForm();
    } catch (err) {
      setFormError(err.message || "Failed to save rule");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- toggle / delete ---------- */
  const onToggle = async (rule) => {
    setBusyId(rule.id);
    try {
      const res = await fetch(`${API}/${rule.id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ is_active: !rule.is_active }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRules((prev) => prev.map((r) => (r.id === rule.id ? json.rule : r)));
    } catch (err) {
      setError(err.message || "Failed to update rule");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    setBusyId(rule.id);
    try {
      const res = await fetch(`${API}/${rule.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
    } catch (err) {
      setError(err.message || "Failed to delete rule");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="dr-page">
      <div className="dr-header">
        <h2>Detection Rules</h2>
        <p className="dr-sub">
          Author your own detection use cases on top of the built-in engine. Condition rules raise
          (or suppress) the verdict deterministically; AI guidance steers the AI analysis toward
          organization-specific scenarios. Suppression can never clear a confirmed threat.
        </p>
      </div>

      {/* kind selector */}
      <div className="dr-kind-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={kind === "condition"}
          className={`dr-kind-tab ${kind === "condition" ? "active" : ""}`}
          onClick={() => { setKind("condition"); setShowForm(false); }}
        >
          <FaProjectDiagram /> Condition rules
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === "ai_guidance"}
          className={`dr-kind-tab ${kind === "ai_guidance" ? "active" : ""}`}
          onClick={() => { setKind("ai_guidance"); setShowForm(false); }}
        >
          <FaRobot /> AI guidance
        </button>
      </div>

      <div className="dr-toolbar">
        <button type="button" className="dr-add-btn" onClick={showForm ? closeForm : openForm}>
          {showForm ? <><FaTimes /> Cancel</> : <><FaPlus /> New {kind === "condition" ? "condition rule" : "AI guidance"}</>}
        </button>
      </div>

      {showForm && (
        <form className="dr-form" onSubmit={onSubmit}>
          {kind === "condition" ? (
            <>
              <div className="dr-form-grid">
                <label className="dr-field">
                  <span>Rule name</span>
                  <input
                    type="text"
                    placeholder="e.g. CEO gift-card fraud"
                    value={conditionForm.name}
                    onChange={(e) => setConditionForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </label>
                <label className="dr-field">
                  <span>Description (optional)</span>
                  <input
                    type="text"
                    placeholder="What does this catch?"
                    value={conditionForm.description}
                    onChange={(e) => setConditionForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </label>
              </div>

              <div className="dr-match-row">
                <span className="dr-match-label">Match</span>
                <select
                  value={conditionForm.match}
                  onChange={(e) => setConditionForm((f) => ({ ...f, match: e.target.value }))}
                >
                  {MATCH_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div className="dr-conditions">
                {conditionForm.conditions.map((c, idx) => (
                  <div className="dr-condition-row" key={idx}>
                    <select value={c.field} onChange={(e) => changeField(idx, e.target.value)}>
                      {FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <select value={c.operator} onChange={(e) => setCond(idx, { operator: e.target.value })}>
                      {operatorsFor(c.field).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {isBooleanField(c.field) ? (
                      <span className="dr-bool-hint">(no value needed)</span>
                    ) : (
                      <input
                        type="text"
                        placeholder="value"
                        value={c.value}
                        onChange={(e) => setCond(idx, { value: e.target.value })}
                      />
                    )}
                    <button
                      type="button"
                      className="dr-cond-remove"
                      onClick={() => removeCond(idx)}
                      disabled={conditionForm.conditions.length === 1}
                      title="Remove condition"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <button type="button" className="dr-add-cond" onClick={addCond}>
                  <FaPlus /> Add condition
                </button>
              </div>

              <div className="dr-action-row">
                <span className="dr-match-label">Action</span>
                <select
                  value={conditionForm.action.effect}
                  onChange={(e) =>
                    setConditionForm((f) => ({ ...f, action: { ...f.action, effect: e.target.value } }))
                  }
                >
                  {RULE_EFFECTS.map((eff) => (
                    <option key={eff.value} value={eff.value}>{eff.label}</option>
                  ))}
                </select>
                <select
                  value={conditionForm.action.verdict}
                  onChange={(e) =>
                    setConditionForm((f) => ({ ...f, action: { ...f.action, verdict: e.target.value } }))
                  }
                >
                  {VERDICTS.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div className="dr-form-grid dr-form-grid-guidance">
              <label className="dr-field">
                <span>Name</span>
                <input
                  type="text"
                  placeholder="e.g. CEO impersonation"
                  value={guidanceForm.name}
                  onChange={(e) => setGuidanceForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="dr-field">
                <span>Description (optional)</span>
                <input
                  type="text"
                  placeholder="Short note for other admins"
                  value={guidanceForm.description}
                  onChange={(e) => setGuidanceForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <label className="dr-field dr-field-full">
                <span>Guidance for the AI analyst</span>
                <textarea
                  rows={4}
                  placeholder="e.g. Flag any email impersonating our CEO or finance team that asks for gift cards, wire transfers, or urgent payment outside normal process."
                  value={guidanceForm.text}
                  onChange={(e) => setGuidanceForm((f) => ({ ...f, text: e.target.value }))}
                />
              </label>
            </div>
          )}

          {formError && <div className="dr-form-error">{formError}</div>}
          <div className="dr-form-actions">
            <button type="submit" className="dr-save-btn" disabled={submitting}>
              {submitting ? "Saving…" : "Save rule"}
            </button>
          </div>
        </form>
      )}

      {error && <div className="dr-error">{error}</div>}

      {loading ? (
        <div className="dr-loading">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="dr-empty">
          {kind === "condition" ? <FaProjectDiagram /> : <FaRobot />}
          <p>
            No {kind === "condition" ? "condition rules" : "AI guidance"} yet. Create the first one
            above.
          </p>
        </div>
      ) : (
        <div className="dr-list">
          {visible.map((r) => (
            <div key={r.id} className={`dr-row ${r.is_active ? "is-active" : "is-inactive"}`}>
              <div className="dr-row-main">
                <div className="dr-row-title">
                  <span className="dr-row-name">{r.name}</span>
                  {r.kind === "condition" && (
                    <span className={`dr-effect-pill dr-effect-${r.definition?.action?.effect}`}>
                      {r.definition?.action?.effect === "suppress" ? "Suppress" : "Raise"} → {r.definition?.action?.verdict}
                    </span>
                  )}
                  {!r.is_active && <span className="dr-inactive-tag">INACTIVE</span>}
                </div>
                {r.description && <div className="dr-row-desc">{r.description}</div>}
                <div className="dr-row-detail">
                  {r.kind === "condition" ? describeRule(r.definition) : r.definition?.text}
                </div>
                <div className="dr-row-meta">
                  Added by {r.created_by || "—"} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="dr-row-actions">
                <button
                  type="button"
                  className={`dr-toggle ${r.is_active ? "on" : "off"}`}
                  onClick={() => onToggle(r)}
                  disabled={busyId === r.id}
                  title={r.is_active ? "Deactivate" : "Reactivate"}
                >
                  {r.is_active ? <FaToggleOn /> : <FaToggleOff />}
                </button>
                <button
                  type="button"
                  className="dr-delete"
                  onClick={() => onDelete(r)}
                  disabled={busyId === r.id}
                  title="Delete"
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
